/**
 * Создание и правка брони.
 *
 * Квартира выбирается из общей базы или вставляется ссылкой — тогда сервер
 * сам заведёт её в базе. Если выбранная квартира в чёрном списке, форма
 * говорит об этом громко и с причиной: ради этого база и ведётся.
 *
 * Черновик (бронь, записанная сама со страницы площадки) открывается этой же
 * формой: всё, что было на странице, уже заполнено, а «Подбирал» и «Кто
 * заселяется» подсвечены и обязательны — с ними черновик становится бронью.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, Link2, Search, X } from 'lucide-react';
import type {
  StayAccountDto,
  StayBookingDto,
  StayPersonDto,
  StayPropertyDto,
} from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { cn } from '@/lib/utils';
import type { StayBookingInput } from '../../lib/api.ts';
import { FormField, INPUT, PlatformMark, PropertyThumb, RatingBadge, StatusPill, TEXTAREA } from './bits.tsx';
import { CURRENCIES, isListingUrl, nightsBetween, nightsLabel, todayIso, accountLabel } from './meta.ts';

export interface BookingPrefill {
  url?: string;
  title?: string;
  accountId?: string;
  property?: StayPropertyDto;
}

interface Props {
  open: boolean;
  booking?: StayBookingDto | null;
  prefill?: BookingPrefill | null;
  accounts: StayAccountDto[];
  people: StayPersonDto[];
  onClose: () => void;
  onSaved: (b: StayBookingDto) => void;
}

/** Поле, которое у черновика ещё предстоит заполнить. */
const NEEDS_INPUT = 'border-warning bg-warning/5 ring-2 ring-warning/20';

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function BookingDialog({ open, booking, prefill, accounts, people, onClose, onSaved }: Props) {
  const { api, user } = useSession();
  const editing = !!booking;
  const draft = !!booking?.draft;

  const [property, setProperty] = useState<StayPropertyDto | null>(null);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [bookingUrl, setBookingUrl] = useState('');
  const [accountId, setAccountId] = useState('');
  const [checkIn, setCheckIn] = useState(todayIso());
  const [checkOut, setCheckOut] = useState(addDays(todayIso(), 1));
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [workerName, setWorkerName] = useState('');
  const [managerId, setManagerId] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [chatUrl, setChatUrl] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'CONFIRMED' | 'CANCELLED'>('CONFIRMED');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<StayPropertyDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Заполнение при открытии: из брони при правке, из подсказки при захвате.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSearch('');
    setFound([]);
    if (booking) {
      setProperty(null);
      setUrl(booking.url ?? '');
      setTitle(booking.title);
      setCity(booking.city ?? '');
      setCountry(booking.country ?? '');
      setBookingUrl(booking.bookingUrl ?? '');
      setAccountId(booking.account?.id ?? '');
      setCheckIn(booking.checkIn);
      setCheckOut(booking.checkOut);
      setPrice(booking.priceMinor === null ? '' : String(booking.priceMinor / 100));
      setCurrency(booking.currency ?? 'EUR');
      setWorkerName(booking.workerName ?? '');
      setManagerId(booking.manager?.id ?? '');
      setConfirmationCode(booking.confirmationCode ?? '');
      setChatUrl(booking.chatUrl ?? '');
      setNote(booking.note ?? '');
      setStatus(booking.status);
      return;
    }
    setProperty(prefill?.property ?? null);
    setUrl(prefill?.property?.url ?? prefill?.url ?? '');
    setTitle(prefill?.property?.title ?? prefill?.title ?? '');
    setCity(prefill?.property?.city ?? '');
    setCountry(prefill?.property?.country ?? '');
    setBookingUrl('');
    setAccountId(prefill?.accountId ?? (accounts.length === 1 ? accounts[0]!.id : ''));
    setCheckIn(todayIso());
    setCheckOut(addDays(todayIso(), 1));
    setPrice('');
    setWorkerName('');
    setManagerId(user?.id ?? '');
    setConfirmationCode('');
    setChatUrl('');
    setNote('');
    setStatus('CONFIRMED');
  }, [open, booking, prefill, accounts, user?.id]);

  // Поиск по базе с задержкой: на каждую букву ходить незачем.
  useEffect(() => {
    if (!open || editing || property || search.trim().length < 2) {
      setFound([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .stayProperties({ q: search.trim() })
        .then((r) => setFound(r.items.slice(0, 6)))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [api, open, editing, property, search]);

  const nights = useMemo(() => nightsBetween(checkIn, checkOut), [checkIn, checkOut]);
  const urlLooksWrong = url.trim() !== '' && !property && !isListingUrl(url.trim());

  const pick = (p: StayPropertyDto) => {
    setProperty(p);
    setUrl(p.url ?? '');
    setTitle(p.title);
    if (!city && p.city) setCity(p.city);
    setSearch('');
    setFound([]);
  };

  const submit = async () => {
    setError(null);
    if (nights < 1) return setError('Выезд должен быть позже заезда');
    if (!title.trim() && !property) return setError('Укажите квартиру: выберите из базы или вставьте ссылку и название');
    if (draft && !managerId) return setError('Кто подбирал квартиру?');
    if (!workerName.trim()) return setError('Кто заселяется?');
    const priceNum = price.trim() === '' ? null : Number(price.replace(',', '.'));
    if (priceNum !== null && (!Number.isFinite(priceNum) || priceNum < 0)) return setError('Стоимость — число');

    const body: StayBookingInput = {
      accountId: accountId || null,
      ...(property ? { propertyId: property.id } : {}),
      title: title.trim() || undefined,
      url: url.trim() || null,
      city: city.trim() || null,
      country: country.trim() || null,
      bookingUrl: bookingUrl.trim() || null,
      checkIn,
      checkOut,
      price: priceNum,
      currency: priceNum === null ? null : currency,
      managerId: managerId || null,
      workerName: workerName.trim(),
      status,
      confirmationCode: confirmationCode.trim() || null,
      chatUrl: chatUrl.trim() || null,
      note: note.trim() || null,
    };
    setSaving(true);
    try {
      const saved = editing
        ? await api.updateStayBooking(booking!.id, body)
        : await api.createStayBooking(body);
      onSaved(saved);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const blacklisted = property?.status === 'BLACKLISTED';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{draft ? 'Заполнить бронь' : editing ? 'Бронь' : 'Новая бронь'}</DialogTitle>
          <DialogDescription>
            {draft
              ? 'Записана автоматически со страницы площадки. Проверьте данные и укажите, кто подбирал и кто заселяется.'
              : editing
                ? 'Изменения видны всем, кто видит эту бронь.'
                : 'Квартира — из базы или по ссылке: новая сама попадёт в базу.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-5">
          {/* ── Квартира ── */}
          <section className="flex flex-col gap-3">
            {property ? (
              <div
                className={cn(
                  'flex items-center gap-3 rounded-xl border p-2.5',
                  blacklisted ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-secondary/40',
                )}
              >
                <PropertyThumb propertyId={property.id} photoUrl={property.photoUrl} platform={property.platform} className="size-14 flex-none rounded-lg" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-heading">{property.title}</p>
                  <p className="mt-0.5 flex items-center gap-2 text-[12px] text-muted-foreground">
                    <PlatformMark platform={property.platform} size="sm" />
                    {property.city ?? 'город не указан'}
                    <RatingBadge rating={property.rating} count={property.ratingCount} />
                  </p>
                </div>
                <StatusPill status={property.status} />
                {!editing && (
                  <Button variant="ghost" size="icon-sm" aria-label="Выбрать другую" onClick={() => setProperty(null)}>
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            ) : (
              !editing && (
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className={cn(INPUT, 'pl-9')}
                    placeholder="Найти в базе: название, город…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {found.length > 0 && (
                    <ul className="absolute inset-x-0 top-full z-10 mt-1 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                      {found.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => pick(p)}
                            className="flex w-full items-center gap-3 rounded-none border-0 bg-transparent px-3 py-2 text-left hover:bg-accent"
                          >
                            <PropertyThumb propertyId={p.id} photoUrl={p.photoUrl} platform={p.platform} className="size-9 flex-none rounded-md" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-medium text-heading">{p.title}</span>
                              <span className="block text-[11px] text-muted-foreground">{p.city ?? '—'}</span>
                            </span>
                            <RatingBadge rating={p.rating} />
                            <StatusPill status={p.status} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            )}

            {blacklisted && (
              <div className="flex gap-2.5 rounded-xl bg-destructive/10 px-3 py-2.5 text-[13px] text-destructive">
                <AlertTriangle className="mt-0.5 size-4 flex-none" />
                <div>
                  <p className="font-semibold">Квартира в чёрном списке</p>
                  <p className="mt-0.5 opacity-90">{property?.statusNote ?? 'Причина не указана — загляните в комментарии.'}</p>
                </div>
              </div>
            )}

            {!property && (
              <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr]">
                <FormField
                  label="Ссылка на квартиру"
                  hint={urlLooksWrong ? 'Это не страница квартиры — в базу не попадёт, но бронь запишется' : undefined}
                >
                  <div className="relative">
                    <Link2 className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <input className={cn(INPUT, 'pl-9')} placeholder="https://www.airbnb.com/rooms/…" value={url} onChange={(e) => setUrl(e.target.value)} disabled={editing} />
                  </div>
                </FormField>
                <FormField label="Название">
                  <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Студия у вокзала" />
                </FormField>
              </div>
            )}
          </section>

          {/* ── Даты и стоимость ── */}
          <section className="grid gap-3 sm:grid-cols-4">
            <FormField label="Заезд">
              <input type="date" className={INPUT} value={checkIn} onChange={(e) => {
                const v = e.target.value;
                setCheckIn(v);
                if (v && nightsBetween(v, checkOut) < 1) setCheckOut(addDays(v, 1));
              }} />
            </FormField>
            <FormField label="Выезд">
              <input type="date" className={INPUT} value={checkOut} min={addDays(checkIn, 1)} onChange={(e) => setCheckOut(e.target.value)} />
            </FormField>
            <FormField label="Стоимость">
              <input className={INPUT} inputMode="decimal" placeholder="0" value={price} onChange={(e) => setPrice(e.target.value)} />
            </FormField>
            <FormField label="Валюта">
              <select className={INPUT} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </FormField>
          </section>
          <p className="-mt-3 flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <CalendarDays className="size-3.5" />
            {nights >= 1 ? nightsLabel(nights) : 'выезд должен быть позже заезда'}
            {nights >= 1 && price.trim() !== '' && Number(price.replace(',', '.')) > 0 && (
              <> · ≈ {(Number(price.replace(',', '.')) / nights).toFixed(0)} {currency} за ночь</>
            )}
          </p>

          {/* ── Кто ── */}
          <section className="grid gap-3 sm:grid-cols-2">
            <FormField label="Кто заселяется">
              <input
                className={cn(INPUT, draft && !workerName.trim() && NEEDS_INPUT)}
                value={workerName}
                onChange={(e) => setWorkerName(e.target.value)}
                placeholder="Имя и фамилия работника"
                autoFocus={draft}
              />
            </FormField>
            <FormField label="Подбирал">
              <select className={cn(INPUT, draft && !managerId && NEEDS_INPUT)} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
                {!managerId && <option value="">— выберите менеджера —</option>}
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                    {p.id === user?.id ? ' (я)' : ''}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Город">
              <input className={INPUT} value={city} onChange={(e) => setCity(e.target.value)} placeholder="Варшава" />
            </FormField>
            <FormField label="Страна">
              <input className={INPUT} value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Польша" />
            </FormField>
            <FormField label="Аккаунт" className="sm:col-span-2">
              <select className={INPUT} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">— не указан —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {accountLabel(a)}
                  </option>
                ))}
              </select>
            </FormField>
          </section>

          {/* ── Детали ── */}
          <details className="group rounded-xl border border-border px-3 py-2" open={editing && !!(confirmationCode || chatUrl || bookingUrl || note)}>
            <summary className="cursor-pointer select-none text-[13px] font-medium text-soft">
              Код брони, ссылки на бронь и переписку, заметка
            </summary>
            <div className="mt-3 grid gap-3 pb-1 sm:grid-cols-2">
              <FormField label="Код подтверждения">
                <input className={INPUT} value={confirmationCode} onChange={(e) => setConfirmationCode(e.target.value)} placeholder="HMXXXXXXX" />
              </FormField>
              <FormField label="Ссылка на переписку с хозяином">
                <input className={INPUT} value={chatUrl} onChange={(e) => setChatUrl(e.target.value)} placeholder="https://…" />
              </FormField>
              <FormField label="Ссылка на бронь на сайте" className="sm:col-span-2">
                <input className={INPUT} value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="Квитанция или детали брони на Airbnb / Booking" />
              </FormField>
              <FormField label="Заметка" className="sm:col-span-2">
                <textarea className={TEXTAREA} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Код домофона, где ключи, договорённости с хозяином" />
              </FormField>
              {editing && (
                <FormField label="Статус">
                  <select className={INPUT} value={status} onChange={(e) => setStatus(e.target.value as 'CONFIRMED' | 'CANCELLED')}>
                    <option value="CONFIRMED">Подтверждена</option>
                    <option value="CANCELLED">Отменена</option>
                  </select>
                </FormField>
              )}
            </div>
          </details>

          {error && (
            <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
              {error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={saving}>
            {saving ? 'Сохраняю…' : draft ? 'Готово' : editing ? 'Сохранить' : 'Записать бронь'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
